import { Box, Separator } from '@chakra-ui/react';
import { useQueryStates } from 'nuqs';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTACT_DRILLDOWN_RESET,
  contactDrawerParsers,
  type ContactPlatform,
} from '../contact-drawer-url-state';
import type { ContactShell } from '../contact-types';
import type { ContactView } from '../contact-views';
import ContactCollaborationsList from './ContactCollaborationsList';
import ContactDetailsPanel from './ContactDetailsPanel';
import ContactIdentityHeader from './ContactIdentityHeader';
import ContactNotesSection from './ContactNotesSection';
import ContactProfilesList from './ContactProfilesList';
import ContactSection from './ContactSection';

interface ContactRailProps {
  contact: ContactShell;
  /**
   * Called when a rail item is chosen. On the stacked mobile layout the shell
   * uses this to push the detail panel in as a second screen. No-op on desktop.
   */
  onSelect?: () => void;
  /**
   * Opens the "propose a collaboration" flow. Omitted (CTA hidden) when the
   * contact has no social profile to invite. Owned by the drawer so the modal
   * survives a mobile screen swap.
   */
  onPropose?: () => void;
  /** Opens the propose flow straight on UGC; omitted for a brand not offered UGC. */
  onProposeUgc?: () => void;
  /** True while the recipient enrichment resolves after a propose click. */
  isProposing?: boolean;
}

/**
 * Left navigation rail of the contact drawer.
 *
 * Owns the single nuqs writer for the drawer's view/platform/collab params (the
 * detail panel reads the same tuple), so all selection state flows through here.
 * Composes the sticky identity header, the PROFILS / COLLABORATIONS lists (both
 * data-driven `ContactNavRow` lists with roving-tabindex), and the collapsible
 * CONTACT section. Each selection also fires `onSelect` so the stacked mobile
 * layout can push the detail screen in.
 */
const ContactRail: React.FC<ContactRailProps> = ({
  contact,
  onSelect,
  onPropose,
  onProposeUgc,
  isProposing,
}) => {
  const { t } = useTranslation();
  const [{ contactView, contactPlatform }, setParams] = useQueryStates(contactDrawerParsers);

  const selectProfile = useCallback(
    (platform: ContactPlatform) => {
      void setParams({
        contactView: 'profile',
        contactPlatform: platform,
        ...CONTACT_DRILLDOWN_RESET,
      });
      onSelect?.();
    },
    [setParams, onSelect]
  );

  const selectCollab = useCallback(
    (view: ContactView, collabId: string) => {
      void setParams({
        contactView: view,
        contactCollab: collabId,
        contactDeliverable: null,
        contactAd: null,
        contactAdCompose: null,
      });
      onSelect?.();
    },
    [setParams, onSelect]
  );

  const openOutreach = useCallback(() => {
    void setParams({
      contactView: 'outreach',
      contactCollab: null,
      contactDeliverable: null,
      contactAd: null,
      contactAdCompose: null,
    });
    // Push the detail screen in on the stacked mobile layout.
    onSelect?.();
  }, [setParams, onSelect]);

  return (
    <Box pb="24px">
      <ContactIdentityHeader contact={contact} platform={contactPlatform} onInvite={openOutreach} />

      <Separator borderColor="border.subtle" mt="18px" />

      <Box px="18px" pt="18px">
        <ContactProfilesList
          contact={contact}
          view={contactView}
          platform={contactPlatform}
          onSelectProfile={selectProfile}
        />
      </Box>

      <Separator borderColor="border.subtle" my="18px" />

      <Box px="18px">
        <ContactCollaborationsList
          contact={contact}
          view={contactView}
          onSelectCollab={selectCollab}
          onPropose={onPropose}
          onProposeUgc={onProposeUgc}
          isProposing={isProposing}
        />
      </Box>

      <Separator borderColor="border.subtle" my="18px" />

      <Box px="18px">
        <ContactSection label={t('contactDrawer.rail.contact.title')} collapsible defaultOpen>
          <ContactDetailsPanel contact={contact.contact} locked={!!contact.locked?.identity} />
        </ContactSection>
      </Box>

      <Separator borderColor="border.subtle" my="18px" />

      <Box px="18px">
        <ContactNotesSection />
      </Box>
    </Box>
  );
};

export default ContactRail;
